import {
  type ProjectMarketResolutionAtResult,
  reduceMarketShareDisplay,
} from '@morfi/backend-sdk-v2/client';
import type { LivelineFramePaintStatus, LivelinePaintedFrame } from '@morfi/chart';

export interface PaintedMarketShareSessionIdentity {
  readonly network: string;
  readonly marketPubkey: string;
  readonly generation: number;
}

export type PaintedMarketShareAvailability =
  | 'inactive'
  | 'pending'
  | 'available'
  | 'degraded'
  | 'paused'
  | 'error';

export interface PaintedMarketShareSnapshot {
  readonly identity: PaintedMarketShareSessionIdentity;
  readonly values: ReadonlyArray<number>;
  readonly threshold: number | null;
  readonly displayedHeadTimeMs: number;
  readonly revision: string;
  readonly morphing: boolean;
  readonly availability: Exclude<PaintedMarketShareAvailability, 'inactive' | 'pending'>;
}

export interface PaintedMarketShareStructure {
  readonly identityKey: string | null;
  readonly availability: PaintedMarketShareAvailability;
  readonly outcomeOrder: ReadonlyArray<number>;
  readonly leaderOutcomeIndex: number | null;
  readonly thresholdReached: boolean;
  readonly forecast: ProjectMarketResolutionAtResult | null;
  readonly forecastUpdating: boolean;
  readonly terminalGeometryRevision: string | null;
}

export interface PaintedMarketShareDisplayStore {
  activate(identity: PaintedMarketShareSessionIdentity): void;
  publish(
    identity: PaintedMarketShareSessionIdentity,
    frame: LivelinePaintedFrame,
    status: LivelineFramePaintStatus,
    metadata?: PaintedMarketSharePublishMetadata,
  ): boolean;
  pause(identity: PaintedMarketShareSessionIdentity): void;
  fail(identity: PaintedMarketShareSessionIdentity): void;
  reset(identity?: PaintedMarketShareSessionIdentity): void;
  getPaintSnapshot(): PaintedMarketShareSnapshot | null;
  getStructureSnapshot(): PaintedMarketShareStructure;
  subscribePaint(listener: () => void): () => void;
  subscribeStructure(listener: () => void): () => void;
}

export interface PaintedMarketSharePublishMetadata {
  readonly forecast: ProjectMarketResolutionAtResult | null;
  readonly terminalGeometryRevision: string | null;
}

const INACTIVE_STRUCTURE: PaintedMarketShareStructure = {
  identityKey: null,
  availability: 'inactive',
  outcomeOrder: [],
  leaderOutcomeIndex: null,
  thresholdReached: false,
  forecast: null,
  forecastUpdating: false,
  terminalGeometryRevision: null,
};

export function paintedMarketShareIdentityKey(identity: PaintedMarketShareSessionIdentity): string {
  return `${identity.network}:${identity.marketPubkey}:${String(identity.generation)}`;
}

export function createPaintedMarketShareDisplayStore(): PaintedMarketShareDisplayStore {
  let activeIdentity: PaintedMarketShareSessionIdentity | null = null;
  let paintSnapshot: PaintedMarketShareSnapshot | null = null;
  let structureSnapshot = INACTIVE_STRUCTURE;
  const paintListeners = new Set<() => void>();
  const structureListeners = new Set<() => void>();

  const replaceStructure = (next: PaintedMarketShareStructure): void => {
    if (sameStructure(structureSnapshot, next)) return;
    structureSnapshot = next;
    for (const listener of structureListeners) listener();
  };

  const setLifecycle = (
    identity: PaintedMarketShareSessionIdentity,
    availability: 'paused' | 'error',
  ): void => {
    if (!sameIdentity(activeIdentity, identity)) return;
    if (paintSnapshot !== null) {
      paintSnapshot = { ...paintSnapshot, availability };
      for (const listener of paintListeners) listener();
    }
    replaceStructure({ ...structureSnapshot, availability });
  };

  return {
    activate(identity: PaintedMarketShareSessionIdentity): void {
      if (sameIdentity(activeIdentity, identity)) return;
      activeIdentity = identity;
      paintSnapshot = null;
      replaceStructure({
        identityKey: paintedMarketShareIdentityKey(identity),
        availability: 'pending',
        outcomeOrder: [],
        leaderOutcomeIndex: null,
        thresholdReached: false,
        forecast: null,
        forecastUpdating: false,
        terminalGeometryRevision: null,
      });
      for (const listener of paintListeners) listener();
    },
    publish(
      identity: PaintedMarketShareSessionIdentity,
      frame: LivelinePaintedFrame,
      status: LivelineFramePaintStatus,
      metadata?: PaintedMarketSharePublishMetadata,
    ): boolean {
      if (!sameIdentity(activeIdentity, identity)) return false;
      const values = paintedValues(frame);
      const threshold = finiteNumber(frame.referenceValue);
      const availability = resolvePaintAvailability(frame);
      paintSnapshot = {
        identity,
        values,
        threshold,
        displayedHeadTimeMs: frame.nowSeconds * 1000,
        revision: frame.revision,
        morphing: status.morphing,
        availability,
      };
      const forecast = metadata?.forecast ?? null;
      const display = reduceMarketShareDisplay({
        values,
        threshold,
        forecast,
        displayedHeadTimeMs: frame.nowSeconds * 1000,
      });
      replaceStructure({
        identityKey: paintedMarketShareIdentityKey(identity),
        availability,
        outcomeOrder: display.outcomeOrder,
        leaderOutcomeIndex: display.leaderOutcomeIndex,
        thresholdReached: display.thresholdReached,
        forecast,
        forecastUpdating: display.forecastUpdating,
        terminalGeometryRevision: metadata?.terminalGeometryRevision ?? null,
      });
      for (const listener of paintListeners) listener();
      return true;
    },
    pause(identity: PaintedMarketShareSessionIdentity): void {
      setLifecycle(identity, 'paused');
    },
    fail(identity: PaintedMarketShareSessionIdentity): void {
      setLifecycle(identity, 'error');
    },
    reset(identity?: PaintedMarketShareSessionIdentity): void {
      if (identity !== undefined && !sameIdentity(activeIdentity, identity)) return;
      activeIdentity = null;
      paintSnapshot = null;
      replaceStructure(INACTIVE_STRUCTURE);
      for (const listener of paintListeners) listener();
    },
    getPaintSnapshot(): PaintedMarketShareSnapshot | null {
      return paintSnapshot;
    },
    getStructureSnapshot(): PaintedMarketShareStructure {
      return structureSnapshot;
    },
    subscribePaint(listener: () => void): () => void {
      paintListeners.add(listener);
      return (): void => {
        paintListeners.delete(listener);
      };
    },
    subscribeStructure(listener: () => void): () => void {
      structureListeners.add(listener);
      return (): void => {
        structureListeners.delete(listener);
      };
    },
  };
}

function sameIdentity(
  left: PaintedMarketShareSessionIdentity | null,
  right: PaintedMarketShareSessionIdentity,
): boolean {
  return (
    left !== null &&
    left.network === right.network &&
    left.marketPubkey === right.marketPubkey &&
    left.generation === right.generation
  );
}

function paintedValues(frame: LivelinePaintedFrame): ReadonlyArray<number> {
  const byIndex = new Map<number, number>();
  for (const series of frame.series) {
    const match = /^outcome-(\d+)$/.exec(series.id);
    if (match === null) continue;
    const outcomeIndex = Number(match[1]);
    if (Number.isSafeInteger(outcomeIndex)) byIndex.set(outcomeIndex, series.value);
  }
  if (byIndex.size === 0) return [];
  const count = Math.max(...byIndex.keys()) + 1;
  return Array.from({ length: count }, (_, index) => byIndex.get(index) ?? Number.NaN);
}

function finiteNumber(value: number | undefined): number | null {
  return value !== undefined && Number.isFinite(value) ? value : null;
}

function resolvePaintAvailability(
  frame: LivelinePaintedFrame,
): 'available' | 'degraded' | 'paused' {
  return frame.opacity !== undefined && frame.opacity < 1 ? 'degraded' : 'available';
}

function sameStructure(
  left: PaintedMarketShareStructure,
  right: PaintedMarketShareStructure,
): boolean {
  return (
    left.identityKey === right.identityKey &&
    left.availability === right.availability &&
    left.leaderOutcomeIndex === right.leaderOutcomeIndex &&
    left.thresholdReached === right.thresholdReached &&
    left.forecast?.outcomeIndex === right.forecast?.outcomeIndex &&
    left.forecast?.crossAtMs === right.forecast?.crossAtMs &&
    left.forecastUpdating === right.forecastUpdating &&
    left.terminalGeometryRevision === right.terminalGeometryRevision &&
    left.outcomeOrder.length === right.outcomeOrder.length &&
    left.outcomeOrder.every((value, index) => value === right.outcomeOrder[index])
  );
}
